// iCalendar (.ics) export of the combined parenting schedule.
//
// Projects the rotation + holiday decisions over a real date window and emits
// one VEVENT per contiguous parenting block ("Jane's parenting time",
// Fri 2:00 pm - Mon 2:00 pm). Holiday-decided days become their own named
// events (e.g. "David - Family Day") so they read clearly in a calendar app.
//
// Times are written as FLOATING local times (no time zone): a 2pm handoff
// means 2pm on the clock wherever the calendar is viewed, matching the
// wall-clock semantics of the rotation resolver across DST changes.
//
// Works in the browser (download button) and in Node (Divii's subscription
// endpoint can call generateIcs() from a Cloud Function and serve the result
// at a tokenized URL — see INTEGRATION.md Phase 4).

import { resolveDay } from './calendarPaint.js'


// Contiguous ownership blocks across [from, to] (inclusive dates, local).
// → [ { partner: 'A'|'B'|'shared'|'split', start: Date, end: Date, label: string|null } ]
//   label = holiday name when the block comes from a holiday decision.
// Unassigned gaps produce no block. Adjacent blocks merge only when both the
// owner and the label match, so holiday-decided days stay separately named.
export function buildParentingBlocks({ rotation, holidays = [], jurisdiction, from, to }) {
  const blocks = []
  let cur = null
  const flush = () => { if (cur) { blocks.push(cur); cur = null } }

  const day = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const endDay = new Date(to.getFullYear(), to.getMonth(), to.getDate())
  while (day.getTime() <= endDay.getTime()) {
    const info = resolveDay(day, rotation, holidays, { jurisdiction, step: 30 })
    // Holiday-owned stretches carry the holiday's name so they stay separate
    // blocks; a paired Eve/Day split therefore lands as a real noon handover
    // instead of collapsing into a whole day.
    const runs = info.runs.map((r) => ({ ...r, label: r.fromHoliday ? r.source : null }))

    for (const r of runs) {
      if (!r.partner) { flush(); continue }
      const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, r.startMin)
      const end = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, r.endMin)
      if (cur && cur.partner === r.partner && cur.label === r.label && cur.end.getTime() === start.getTime()) {
        cur.end = end
      } else {
        flush()
        cur = { partner: r.partner, start, end, label: r.label }
      }
    }
    day.setDate(day.getDate() + 1)
  }
  flush()
  return blocks
}

function pad(n) { return String(n).padStart(2, '0') }

// Floating local time: 20260727T140000
function icsLocal(d) {
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`
}

// UTC stamp: 20260727T140000Z
function icsUtc(d) {
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
}

// RFC 5545 3.1: a content line is at most 75 octets, continued by CRLF plus
// one leading space. Our DESCRIPTION lines run past 200 and strict parsers
// reject the file rather than wrapping it themselves. Measured in octets, not
// characters: an accented name is multi-byte in UTF-8 and a fold landing
// mid-sequence corrupts it.
function foldLine(line) {
  const bytes = Buffer.from(line, 'utf8')
  if (bytes.length <= 75) return line

  const parts = []
  let start = 0
  let limit = 75
  while (start < bytes.length) {
    let end = Math.min(start + limit, bytes.length)
    // Never split a UTF-8 sequence; continuation bytes are 10xxxxxx.
    while (end > start && end < bytes.length && (bytes[end] & 0xc0) === 0x80) end -= 1
    parts.push(bytes.slice(start, end).toString('utf8'))
    start = end
    limit = 74 // a continuation line spends one octet on its leading space
  }
  return parts.join('\r\n ')
}

function escapeText(s) {
  return String(s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
}

function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
}

function icsDate(d) {
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`
}

// generateRecurringIcs({ rotation, holidays, jurisdiction, partyAName,
//   partyBName, childrenLabel, from, until, now, forParty, calendarName,
//   uidSuffix })
// → VCALENDAR where the regular rotation is a handful of RECURRING series
//   (one VEVENT with RRULE:FREQ=WEEKLY;INTERVAL=<rotation length> per
//   contiguous block of the cycle) and holiday-decided days are separate
//   one-off all-day events that override the pattern.
//
// Why series: when the parties agree on a new arrangement mid-year, the
// client deletes a few SERIES from their calendar (one action each) instead
// of hundreds of individual entries, then imports the new file.
export function generateRecurringIcs(opts) {
  const {
    rotation, holidays = [], jurisdiction,
    partyAName, partyBName, childrenLabel,
    from, until, now = new Date(0),
    forParty, calendarName, uidSuffix = 'divii.ca',
    // Calendar color hint: { css: 'teal', hex: '#34555f' }. Emitted as the
    // RFC 7986 COLOR property (CSS color name) plus X-APPLE-CALENDAR-COLOR
    // (hex, honoured by Apple Calendar on import/subscription) and per-event
    // COLOR for clients that support it. Google/Outlook fall back to the
    // color the user assigns the imported calendar.
    color = null,
  } = opts

  const aName = partyAName || 'Party A'
  const bName = partyBName || 'Party B'
  const N = rotation?.weeks?.length || 0
  const includes = (p) => !(forParty === 'A' || forParty === 'B') || p === forParty || p === 'shared' || p === 'split'

  const name = calendarName
    || (childrenLabel ? `Parenting time with ${childrenLabel}` : 'Parenting time schedule')
  const stamp = icsUtc(now)
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Divii//Parenting Time Schedule//EN',
    'CALSCALE:GREGORIAN',
    `NAME:${escapeText(name)}`,
    `X-WR-CALNAME:${escapeText(name)}`,
  ]
  if (color?.css) lines.push(`COLOR:${color.css}`)
  if (color?.hex) lines.push(`X-APPLE-CALENDAR-COLOR:${color.hex}`)
  const eventColor = () => { if (color?.css) lines.push(`COLOR:${color.css}`) }

  // --- The regular rotation, as recurring series -------------------------
  if (N > 0 && rotation.anchorDate) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(rotation.anchorDate))
    const anchor = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
    if (anchor) {
      const cycleDays = N * 7
      const fromMid = new Date(from.getFullYear(), from.getMonth(), from.getDate())
      const diffDays = Math.round((fromMid.getTime() - anchor.getTime()) / 86_400_000)
      // Anchor the series one full cycle BEFORE the cycle containing `from`,
      // so blocks that wrap across the cycle boundary already cover the days
      // right after `from`.
      const cycleStart = addDays(anchor, (Math.floor(diffDays / cycleDays) - 1) * cycleDays)
      // Rotation-only blocks (no holiday carve-outs — holidays are exported
      // separately as overrides). The window starts a full cycle EARLIER so a
      // block that wraps across cycleStart merges naturally (no artificial
      // fragment at the boundary), and extends a cycle PAST it so wrapping
      // blocks keep their full length. We then keep one cycle's worth of
      // block starts — each becomes one recurring series.
      const blocks = buildParentingBlocks({
        rotation, holidays: [],
        from: addDays(cycleStart, -cycleDays), to: addDays(cycleStart, cycleDays * 2 - 1),
      })
      const cycleEnd = addDays(cycleStart, cycleDays)
      for (const b of blocks) {
        if (b.start < cycleStart || b.start >= cycleEnd) continue
        if (!includes(b.partner)) continue
        const who = b.partner === 'A' ? aName : b.partner === 'B' ? bName : 'Shared'
        lines.push(
          'BEGIN:VEVENT',
          `UID:divii-series-${icsLocal(b.start)}-${b.partner}@${uidSuffix}`,
          `DTSTAMP:${stamp}`,
          `DTSTART:${icsLocal(b.start)}`,
          `DTEND:${icsLocal(b.end)}`,
          `RRULE:FREQ=WEEKLY;INTERVAL=${N}${until ? `;UNTIL=${icsLocal(until)}` : ''}`,
          `SUMMARY:${escapeText(`${who}'s parenting time`)}`,
          `DESCRIPTION:${escapeText(`Repeats every ${N === 1 ? 'week' : `${N} weeks`} per the regular parenting rotation. Holiday decisions appear as separate events and override this schedule. If the arrangement changes, delete this series and import the new schedule.`)}`,
        )
        eventColor()
        lines.push('END:VEVENT')
      }
    }
  }

  // --- Holiday decisions, as one-off override events ---------------------
  // Whole-day decisions are all-day events. Paired Eve/Day blocks run noon to
  // noon, so they're emitted as timed events and merged across the midnight
  // they straddle (one event, noon Dec 24 → noon Dec 25).
  if (until) {
    let cur = null
    let timed = null
    const flushTimed = () => {
      if (!timed) return
      if (includes(timed.who)) {
        const who = timed.who === 'A' ? aName : timed.who === 'B' ? bName : (timed.who === 'shared' ? 'Shared' : 'Split')
        lines.push(
          'BEGIN:VEVENT',
          `UID:divii-holiday-${icsLocal(timed.start)}-${timed.who}@${uidSuffix}`,
          `DTSTAMP:${stamp}`,
          `DTSTART:${icsLocal(timed.start)}`,
          `DTEND:${icsLocal(timed.end)}`,
          `SUMMARY:${escapeText(`${who} — ${timed.name}`)}`,
          `DESCRIPTION:${escapeText(`Per the ${timed.name} decision in the Holiday Schedule. Overrides the regular rotation for this block.`)}`,
        )
        eventColor()
        lines.push('END:VEVENT')
      }
      timed = null
    }
    const flushHoliday = () => {
      if (!cur) return
      if (includes(cur.who)) {
        const who = cur.who === 'A' ? aName : cur.who === 'B' ? bName : (cur.who === 'shared' ? 'Shared' : 'Split')
        lines.push(
          'BEGIN:VEVENT',
          `UID:divii-holiday-${icsDate(cur.start)}-${cur.who}@${uidSuffix}`,
          `DTSTAMP:${stamp}`,
          `DTSTART;VALUE=DATE:${icsDate(cur.start)}`,
          `DTEND;VALUE=DATE:${icsDate(addDays(cur.end, 1))}`, // DTEND is exclusive for all-day events
          `SUMMARY:${escapeText(`${who} — ${cur.name}`)}`,
          `DESCRIPTION:${escapeText(`Per the ${cur.name} decision in the Holiday Schedule. Overrides the regular rotation for ${cur.who === 'A' || cur.who === 'B' ? `these dates: the children are with ${who}` : 'these dates'}.`)}`,
        )
        eventColor()
        lines.push('END:VEVENT')
      }
      cur = null
    }
    let day = new Date(from.getFullYear(), from.getMonth(), from.getDate())
    const lastDay = new Date(until.getFullYear(), until.getMonth(), until.getDate())
    while (day.getTime() <= lastDay.getTime()) {
      const info = resolveDay(day, rotation, holidays, { jurisdiction, step: 30 })
      if (info.holidayWho) {
        flushTimed()
        if (cur && cur.who === info.holidayWho && cur.name === info.holidayName && addDays(cur.end, 1).getTime() === day.getTime()) {
          cur.end = new Date(day)
        } else {
          flushHoliday()
          cur = { who: info.holidayWho, name: info.holidayName, start: new Date(day), end: new Date(day) }
        }
      } else {
        flushHoliday()
        for (const r of info.runs) {
          if (!r.fromHoliday || !r.partner) continue
          const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, r.startMin)
          const end = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, r.endMin)
          if (timed && timed.who === r.partner && timed.name === r.source && timed.end.getTime() === start.getTime()) {
            timed.end = end
          } else {
            flushTimed()
            timed = { who: r.partner, name: r.source, start, end }
          }
        }
      }
      day = addDays(day, 1)
    }
    flushHoliday()
    flushTimed()
  }

  lines.push('END:VCALENDAR')
  return lines.map(foldLine).join('\r\n') + '\r\n'
}

// generateIcs({ rotation, holidays, jurisdiction, partyAName, partyBName,
//               childrenLabel, from, to, now, forParty, calendarName, uidSuffix })
// → full VCALENDAR string (CRLF line endings).
//   forParty: 'A' | 'B' | undefined — filter to one parent's blocks (shared/
//   split blocks are always included; both parents should see those).
export function generateIcs(opts) {
  const {
    rotation, holidays = [], jurisdiction,
    partyAName, partyBName, childrenLabel,
    from, to, now = new Date(0),
    forParty, calendarName, uidSuffix = 'divii.ca',
  } = opts

  const aName = partyAName || 'Party A'
  const bName = partyBName || 'Party B'
  let blocks = buildParentingBlocks({ rotation, holidays, jurisdiction, from, to })
  if (forParty === 'A' || forParty === 'B') {
    blocks = blocks.filter((b) => b.partner === forParty || b.partner === 'shared' || b.partner === 'split')
  }

  const name = calendarName
    || (childrenLabel ? `Parenting time with ${childrenLabel}` : 'Parenting time schedule')
  const stamp = icsUtc(now)

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Divii//Parenting Time Schedule//EN',
    'CALSCALE:GREGORIAN',
    `NAME:${escapeText(name)}`,
    `X-WR-CALNAME:${escapeText(name)}`,
  ]

  for (const b of blocks) {
    const who = b.partner === 'A' ? aName : b.partner === 'B' ? bName : null
    const summary = b.label
      ? (who ? `${who} — ${b.label}` : `${b.partner === 'shared' ? 'Shared' : 'Split'} — ${b.label}`)
      : (who ? `${who}'s parenting time` : `${b.partner === 'shared' ? 'Shared' : 'Split'} parenting time`)
    const description = b.label
      ? `Per the ${b.label} decision in the Holiday Schedule.`
      : 'Per the weekly parenting rotation.'
    lines.push(
      'BEGIN:VEVENT',
      `UID:divii-parenting-${icsLocal(b.start)}-${b.partner}@${uidSuffix}`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${icsLocal(b.start)}`,
      `DTEND:${icsLocal(b.end)}`,
      `SUMMARY:${escapeText(summary)}`,
      `DESCRIPTION:${escapeText(description)}`,
      'END:VEVENT',
    )
  }

  lines.push('END:VCALENDAR')
  return lines.map(foldLine).join('\r\n') + '\r\n'
}
